import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import Waveform from "@/components/Waveform";

function DropCard({ drop, index }) {
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const join = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Enter a valid email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/drops/waitlist", { email, drop_id: drop.id });
      setCode(data.code);
      setJoined(true);
    } catch {
      toast.error("Could not join the waitlist. Try again.");
    }
    setLoading(false);
  };

  const live = drop.status === "live";

  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.7, delay: index * 0.1 }}
      className={`border p-10 sm:p-14 ${live ? "border-pulse/50" : "border-border"}`}
      data-testid={`drop-card-${drop.id}`}>
      <div className="flex items-center gap-4 mb-6">
        <span className={`font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 border ${
          live ? "text-pulse border-pulse/50" : "text-signal border-signal/40"
        }`} data-testid={`drop-status-${drop.id}`}>
          {live ? "● Live now" : `Upcoming — ${drop.eta}`}
        </span>
      </div>
      <h2 className="font-anton uppercase text-5xl sm:text-7xl leading-[0.9]">{drop.name}</h2>
      {drop.tagline && <p className="text-graphite text-lg mt-5 max-w-xl leading-[1.85]">{drop.tagline}</p>}

      {live ? (
        <Link to="/shop" className="btn-pulse inline-flex mt-10" data-testid={`drop-shop-${drop.id}`}>
          Shop the Drop <ArrowRight size={16} />
        </Link>
      ) : joined ? (
        <div className="mt-10" data-testid={`waitlist-success-${drop.id}`}>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-signal">You're on the list — first access when it goes live.</p>
          <div className="mt-5 inline-block border border-dashed border-signal px-8 py-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-graphite mb-1">Your 10% code</p>
            <p className="font-anton text-2xl tracking-[0.25em] text-signal" data-testid={`waitlist-code-${drop.id}`}>{code}</p>
          </div>
          <p className="text-graphite text-xs mt-4 font-mono">Also sent to your inbox. Apply it at checkout — valid now.</p>
        </div>
      ) : (
        <div className="mt-10 max-w-md">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-chalk/80 mb-4">
            Join the waitlist — get an instant <span className="text-signal">10% off code</span>
          </p>
          <form onSubmit={join} className="flex gap-3" data-testid={`waitlist-form-${drop.id}`}>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com" data-testid={`waitlist-email-${drop.id}`}
              className="flex-1 bg-transparent border-b border-border py-3 font-mono text-sm focus:border-signal outline-none" />
            <button type="submit" disabled={loading} data-testid={`waitlist-join-${drop.id}`}
              className="font-mono text-[11px] uppercase tracking-wider border border-chalk px-5 hover:bg-chalk hover:text-ink transition-colors disabled:opacity-60">
              {loading ? "…" : "Join"}
            </button>
          </form>
        </div>
      )}
    </motion.div>
  );
}

export default function Drops() {
  const [drops, setDrops] = useState([]);

  useEffect(() => {
    api.get("/drops").then((r) => setDrops(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-ink min-h-screen pt-32 pb-28" data-testid="drops-page">
      <div className="container-sync">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="eyebrow mb-4">Before everyone else</p>
          <h1 className="font-anton uppercase text-6xl sm:text-8xl leading-[0.85]">Limited<br />Drops</h1>
          <p className="text-graphite text-lg mt-6 max-w-xl leading-[1.85]">
            Small runs. One release. Gone for good. Join a waitlist and you'll hear it first —
            plus an instant 10% code to use on anything.
          </p>
        </motion.div>
        <Waveform color="rgb(var(--graphite))" className="my-14" />
        <div className="space-y-8">
          {drops.map((d, i) => <DropCard key={d.id} drop={d} index={i} />)}
        </div>
      </div>
    </div>
  );
}
